export type UtmParams = Record<string, string>

const UTM_STORAGE_KEY = "healmeUtmParams"
const ATTRIBUTION_QUERY_KEYS = new Set(["fbclid", "fb_login_id", "gclid", "msclkid", "ttclid"])

const isBrowser = (): boolean => typeof window !== "undefined"

const getStorage = (): Storage | null => {
  if (!isBrowser()) {
    return null
  }

  // Accessing `window.localStorage` throws SecurityError in sandboxed iframes, some in-app
  // browsers, and locked-down Chrome/Android profiles — not just when it's undefined.
  try {
    return window.localStorage
  } catch {
    return null
  }
}

const isTrackedAttributionParam = (key: string): boolean => key.startsWith("utm_") || ATTRIBUTION_QUERY_KEYS.has(key)

const isUtmParams = (value: unknown): value is UtmParams => {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    return false
  }

  return Object.values(value).every((entry) => typeof entry === "string")
}

export const extractUtmParamsFromSearch = (search = ""): UtmParams => {
  const searchParams = new URLSearchParams(search)
  const utmParams: UtmParams = {}

  searchParams.forEach((value, key) => {
    if (!isTrackedAttributionParam(key) || !value) {
      return
    }

    utmParams[key] = value
  })

  return utmParams
}

export const getStoredUtmParams = (): UtmParams => {
  const storage = getStorage()

  if (!storage) {
    return {}
  }

  try {
    const storedValue = storage.getItem(UTM_STORAGE_KEY)

    if (!storedValue) {
      return {}
    }

    const parsedValue: unknown = JSON.parse(storedValue)

    if (!isUtmParams(parsedValue)) {
      return {}
    }

    return parsedValue
  } catch {
    return {}
  }
}

export const persistUtmParamsFromUrl = (search = isBrowser() ? window.location.search : ""): UtmParams => {
  const utmParams = extractUtmParamsFromSearch(search)
  const storage = getStorage()

  if (Object.keys(utmParams).length === 0) {
    return getStoredUtmParams()
  }

  if (!storage) {
    return utmParams
  }

  try {
    storage.setItem(UTM_STORAGE_KEY, JSON.stringify(utmParams))
  } catch {
    return utmParams
  }

  return utmParams
}
